<script setup lang="ts">
import type { Suggestion } from '@/js/types/dadata.ts';
import type { LeafletLineRef, LeafletMarkerRef } from '@/js/vue/composable/leaflet-map.ts';
import type { LeafletMouseEvent, Map } from 'leaflet';
import { getGeocoding, getAddressSuggests } from '@/js/api/dadata.ts';
import mkad from '@/js/data/coords/mkad-out.ts';
import { getLocaleValue } from '@/js/helper/locale.ts';
import {
  convertCoordinates,
  findClosestPointOnMkadPolygon,
  isPointInMkadPolygon,
  isPointInMosoblPolygon,
} from '@/js/turf.ts';
import { classname } from '@/js/utils';
import BaseButton from '@/js/vue/base/base-button.vue';
import AddressSuggestions from '@/js/vue/checkout/address-suggestions.vue';
import { getCourierDeliveryValidationError, getDadataAddressParams } from '@/js/vue/checkout/data.ts';
import { useLeafletMap } from '@/js/vue/composable/leaflet-map.ts';
import LeafletMap from '@/js/vue/map/leaflet-map.vue';
import FancyModal from '@/js/vue/modal/fancy-modal.vue';
import { useDebounceFn } from '@vueuse/core';
import { onBeforeUnmount, ref, shallowRef, watch } from 'vue';

const MOSCOW_CENTER = [37.6173, 55.7558];

const cn = classname('delivery-address-modal');

const shown = defineModel<boolean>('shown', { default: false });
const address = defineModel<string>('address', { default: '' });
const isInsideDelivery = defineModel<boolean>('isInsideDelivery', { default: false });
const isOutsideDelivery = defineModel<boolean>('isOutsideDelivery', { default: false });
const distance = defineModel<number>('distance', { default: 0 });

const { getCarRoute, createLine, createLineByRoute } = useLeafletMap();
const mapInstance = shallowRef<Map>();
const point = ref<number[]>(MOSCOW_CENTER);
const deliveryMarker = shallowRef<LeafletMarkerRef>();
const addressSuggestions = ref<Suggestion[]>([]);
const mkadPoliline = ref<LeafletLineRef>();
const carRouteLine = ref<LeafletLineRef>();
const addressError = ref<string | null>(null);
let locationRequestId = 0;
let skipNextBlurSync = false;

function resolveSuggestion(suggestions: Suggestion[], value: string): Suggestion | undefined {
  const trimmed = value.trim();
  if (!trimmed || suggestions.length === 0) {
    return undefined;
  }

  const exact = suggestions.find(item => item.value === trimmed);
  if (exact) {
    return exact;
  }

  const normalized = trimmed.toLowerCase();
  return suggestions.find(item => item.value.toLowerCase() === normalized);
}

function clearCarRouteLine() {
  carRouteLine.value?.destroy();
  carRouteLine.value = undefined;
}

function destroyMapLayers() {
  locationRequestId += 1;
  clearCarRouteLine();
  mkadPoliline.value?.destroy();
  mkadPoliline.value = undefined;
}

const debouncedCarRoute = useDebounceFn((start, end) => getCarRoute(start, end), 1000);

async function getRouteToMkad(coors: number[]) {
  try {
    const startPoint = findClosestPointOnMkadPolygon(coors);
    const response = await debouncedCarRoute({ lon: startPoint[0], lat: startPoint[1] }, { lon: coors[0], lat: coors[1] });
    if (response.data && response.data.result.length > 0) {
      return response.data.result[0];
    }
    return null;
  }
  catch (e) {
    console.warn(e);
  }
}

async function showCarRouteLine(coors: number[]) {
  const response = await getRouteToMkad(coors);
  if (response && mapInstance.value) {
    carRouteLine.value = createLineByRoute(mapInstance.value, response);
    distance.value = response.total_distance;
  }
}

function updateDeliveryZone(coors: number[]) {
  isInsideDelivery.value = isPointInMkadPolygon(coors);
  isOutsideDelivery.value = isPointInMosoblPolygon(coors);
}

function updateSuggestions(suggestions: Suggestion[]) {
  addressSuggestions.value = suggestions;
}

async function applySuggestion(suggestion: Suggestion) {
  skipNextBlurSync = true;
  const lon = Number(suggestion.data.geo_lon);
  const lat = Number(suggestion.data.geo_lat);
  if (!lon || !lat) {
    return;
  }

  const requestId = ++locationRequestId;
  const coors = [lon, lat];

  if (address.value !== suggestion.value) {
    address.value = suggestion.value;
  }

  await applyCoordinates(coors);
  if (requestId !== locationRequestId) {
    return;
  }
  mapInstance.value?.setView([coors[1], coors[0]], mapInstance.value.getZoom());
}

async function syncAddressOnBlur() {
  if (skipNextBlurSync) {
    skipNextBlurSync = false;
    return;
  }

  const value = address.value.trim();
  if (!value) {
    return;
  }

  const requestId = ++locationRequestId;
  let suggestion = resolveSuggestion(addressSuggestions.value, value);

  if (!suggestion) {
    const { data } = await getAddressSuggests(value, getDadataAddressParams());
    if (requestId !== locationRequestId) {
      return;
    }
    if (data?.suggestions?.length) {
      addressSuggestions.value = data.suggestions;
      suggestion = resolveSuggestion(data.suggestions, value);
    }
  }

  if (suggestion && requestId === locationRequestId) {
    await applySuggestion(suggestion);
  }
}

async function applyCoordinates(coors: number[]) {
  point.value = coors;
  deliveryMarker.value?.setCoordinates(coors);
  updateDeliveryZone(coors);
  clearCarRouteLine();
  distance.value = 0;
  if (isOutsideDelivery.value) {
    await showCarRouteLine(coors);
  }
}

watch([address, isInsideDelivery, isOutsideDelivery], () => {
  addressError.value = null;
});

watch(isOutsideDelivery, (showMkadLine) => {
  if (showMkadLine && mapInstance.value) {
    const coors = convertCoordinates(mkad.coordinates);
    mkadPoliline.value = createLine(mapInstance.value, coors[0]);
  }
  else {
    mkadPoliline.value?.destroy();
    mkadPoliline.value = undefined;
  }
});

async function onMapClick(e: LeafletMouseEvent) {
  const requestId = ++locationRequestId;
  const coors = [e.latlng.lng, e.latlng.lat];
  await applyCoordinates(coors);
  const { data } = await getGeocoding(coors[0], coors[1]);
  if (requestId !== locationRequestId) {
    return;
  }
  if (data?.suggestions.length) {
    address.value = data.suggestions[0].value;
  }
}

watch(mapInstance, (map, prevMap) => {
  prevMap?.off('click', onMapClick);
  if (map) {
    map.on('click', onMapClick);
  }
});

watch(shown, (isShown) => {
  if (!isShown) {
    destroyMapLayers();
  }
});

onBeforeUnmount(() => {
  mapInstance.value?.off('click', onMapClick);
  destroyMapLayers();
});

function onModalOpened() {
  requestAnimationFrame(() => {
    mapInstance.value?.invalidateSize();
  });
}

function confirm() {
  const error = getCourierDeliveryValidationError(
    address.value,
    isInsideDelivery.value,
    isOutsideDelivery.value,
  );
  if (error) {
    addressError.value = error;
    return;
  }
  shown.value = false;
}
</script>

<template>
  <FancyModal
    v-model:shown="shown"
    container-class="delivery-address-modal-container"
    @opened="onModalOpened"
  >
    <template #heading>
      {{ getLocaleValue('delivery_address') }}
    </template>
    <div :class="cn()">
      <div :class="cn('search')">
        <AddressSuggestions
          v-model="address"
          :params="getDadataAddressParams()"
          :placeholder="getLocaleValue('enter_your_shipping_address')"
          required
          @update:suggestions="updateSuggestions"
          @select="applySuggestion"
          @blur="syncAddressOnBlur"
        />
        <p v-if="addressError" :class="cn('error')">
          {{ addressError }}
        </p>
        <BaseButton theme="accent" :class="cn('confirm')" @click="confirm">
          Готово
        </BaseButton>
      </div>
      <div :class="cn('map')">
        <LeafletMap
          v-model:map="mapInstance"
          v-model:marker="deliveryMarker"
          :center="point"
        />
      </div>
    </div>
  </FancyModal>
</template>

<style lang="scss">
.delivery-address-modal-container {
  width: 100%;
  max-width: 95vw;
}

.delivery-address-modal {
  display: grid;
  grid-template-columns: 3fr 4fr;
  align-items: start;

  &__search {
    display: flex;
    flex-direction: column;
    gap: 16px;
    height: 100%;
    padding: 24px;
    z-index: 2;
  }

  &__error {
    margin: 0;
    color: #e53935;
    font-size: 14px;
  }

  &__confirm {
    width: 100%;
    margin-top: auto;
  }

  &__map {
    min-height: 500px;
    z-index: 1;
  }

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}
</style>
